<template>
  <BaseDrawer
    :is-open="isOpen"
    shrink
    :is-loading="isLoading"
    @close="emit('close')"
  >
    <template v-if="step === 'initial'">
      <DrawerHeader @close="emit('close')">
        <div class="flex items-center">
          <template
            v-if="transaction.status === 'successful'"
          >
            <span> {{ typeLabel }} Successful </span>
            <IconCheckCircle class="ml-2 text-positive" />
          </template>
          <template
            v-else-if="transaction.status === 'failed'"
          >
            <span> {{ typeLabel }} Failed </span>
            <IconXCircle class="ml-2 text-error" />
          </template>
          <template v-else>
            <span>{{ typeLabel }} {{ statusLabel }}</span>
            <IconExclamationCircle
              class="ml-2 text-warning"
            />
          </template>
        </div>
      </DrawerHeader>
      <div class="flex-1 overflow-y-auto px-4 pb-4">
        <template
          v-if="transaction.status === 'successful'"
        >
          <template v-if="transaction.type === 'payment'">
            <ActionItemSmall
              label="Refund Payment"
              :icon="IconRefund"
              @click="onClickRefund"
            />
          </template>
          <ActionItemSmall
            v-if="isTransactionVoidable"
            :label="`Void ${typeLabel}`"
            :icon="IconVoidTransaction"
            @click="onClickVoid"
          />
        </template>

        <template
          v-else-if="transaction.type === 'payment'"
        >
          <MoreMenuSubsection v-if="showRetry">
            <template v-if="cardholderNotPresentEnabled">
              <ActionItemSmall
                label="Retry Payment (Card)"
                :icon="IconCard"
                @click="retryPayment(false)"
              />
              <ActionItemSmall
                label="Retry Payment (CNP)"
                :icon="IconPhoneFilled"
                @click="retryPayment(true)"
              />
            </template>
            <ActionItemSmall
              v-else
              label="Retry Payment"
              :icon="IconRetryTransaction"
              @click="retryPayment(false)"
            />
          </MoreMenuSubsection>
        </template>

        <ActionItemSmall
          v-if="printingSupported"
          label="Print Merchant Receipt"
          :icon="IconPrintReceipt"
          @click="onPrintReceipt(true)"
        />

        <ActionItemSmall
          label="Email Receipt"
          :icon="IconEmailReceipt"
          @click="step = 'emailReceipt'"
        />
        <ActionItemSmall
          v-if="organizationConfig?.smsServiceEnabled"
          label="SMS Receipt"
          :icon="IconSMSReceipt"
          @click="step = 'smsReceipt'"
        />
        <ActionItemSmall
          v-if="printingSupported"
          label="Print Receipt"
          :icon="IconPrintReceipt"
          @click="onPrintReceipt()"
        />
      </div>
    </template>

    <RefundContent
      v-else-if="step === 'refund'"
      :transaction-id="transaction.id"
      @failed="step = 'initial'"
      @success="emit('close')"
    />

    <div v-else-if="step === 'emailReceipt'">
      <DrawerHeader @close="step = 'initial'">
        <div class="flex items-center"> Email Receipt </div>
      </DrawerHeader>
      <ReceiptEmailForm
        class="px-4 pb-4"
        :object-id="transaction.id"
        receipt-type="transaction"
      />
    </div>

    <div v-else-if="step === 'smsReceipt'">
      <DrawerHeader @close="step = 'initial'">
        <div class="flex items-center"> SMS Receipt </div>
      </DrawerHeader>
      <ReceiptSMSForm
        class="px-4 pb-4"
        :object-id="transaction.id"
        receipt-type="transaction"
      />
    </div>

    <BaseConfirmation
      ref="refundConfirmationRef"
      title="Transaction void failed. Would you like to try a refund instead?"
      confirm-label="Refund"
      cancel-label="Cancel"
    />

    <NestedSupervisorLoginDrawer
      ref="supervisorLoginDrawerRef"
    />
  </BaseDrawer>
</template>

<script setup lang="ts">
import {
  IconPhoneFilled,
  IconCard,
  IconEmailReceipt,
  IconSMSReceipt,
  IconPrintReceipt,
  IconRefund,
  IconRetryTransaction,
  IconVoidTransaction,
  IconCheckCircle,
  IconXCircle,
  IconExclamationCircle,
} from "@/icons"

import * as Sentry from "@sentry/vue"

import {
  createTransaction,
  type CreateTransactionResponse,
} from "@/services/createTransaction"
import { IPayment } from "@/androidPlugins/IPayment"
import { retrieveTransactionReceipt } from "@/services/transactions/retrieveTransactionReceipt"
import { IPrinter } from "@/androidPlugins/IPrinter"
import { pascalize } from "humps"
import { voidTransaction } from "@/services/transactions/voidTransaction"
import { useOrganizationConfig } from "@/services/organizations/retrieveOrganizationConfig"
import type {
  OrderDetailPayment,
  OrderDetailRefund,
} from "@/services/orders/retrieveOrder"
import type { ListTransactionsResponseItem } from "@/services/transactions/listTransactions"
import type { CreateRefundResponse } from "@/services/createRefund"

const { startLoading, stopLoading } =
  usePageBlockingLoading()
const { isSupervisor } = useAuth()
const {
  isCardReceiptsEnabled,
  gratuityIsAvailable,
  tenant,
} = useTenant()

const { cardholderNotPresentEnabled } =
  useCardholderNotPresent()
const { printingSupported, serialNumber } = useDevice()
const { organizationConfig } = useOrganizationConfig()

const props = defineProps<{
  transaction:
    | CreateTransactionResponse
    | OrderDetailPayment
    | OrderDetailRefund
    | ListTransactionsResponseItem
    | CreateRefundResponse
  isOpen: boolean
  showRetry?: boolean
}>()

const supervisorLoginDrawerRef = useTemplateRef(
  "supervisorLoginDrawerRef"
)

const refundConfirmationRef = useTemplateRef(
  "refundConfirmationRef"
)

watch(
  () => props.isOpen,
  (value) => {
    if (value === true) {
      step.value = "initial"
    }
  }
)

const emit = defineEmits(["close"])

const step = ref<
  "initial" | "refund" | "emailReceipt" | "smsReceipt"
>("initial")
const isLoading = ref(false)
const retryPayment = async (
  cardholderNotPresent: boolean
) => {
  isLoading.value = true
  try {
    const devicePaymentResponse =
      await IPayment.makeDevicePayment({
        amountTrans:
          props.transaction.amountTransactedGross,
        amountGratuity:
          props.transaction.amountTransactedGratuity,
        cardholderNotPresent,
        gratuityIsAvailable: gratuityIsAvailable.value,
        disablePrinting:
          !isCardReceiptsEnabled.value ||
          !printingSupported.value,
      })

    const transaction = await createTransaction(
      props.transaction.invoiceId,
      {
        data: devicePaymentResponse.data,
        source: "pos_card",
      }
    )
    await maybePrintMerchantReceipt(transaction.id)
  } catch {
    toast.error("Failed to create transaction")
  } finally {
    isLoading.value = false
  }

  emit("close")
}

const onPrintReceipt = async (isMerchant = false) => {
  isLoading.value = true
  try {
    const { receipt, webReceiptUrl } =
      await retrieveTransactionReceipt(
        props.transaction.id,
        { isMerchant }
      )
    await IPrinter.printStr({
      str: receipt,
      footerQrCodeContent: webReceiptUrl,
    })
    emit("close")
  } catch {
    toast.error("Failed to generate receipt")
  } finally {
    isLoading.value = false
  }
}

watch(
  () => props.isOpen,
  (newValue, oldValue) => {
    if (newValue && !oldValue) {
      step.value = "initial"
    }
  }
)

const typeLabel = computed(
  () =>
    (
      ({
        payment: "Payment",
        refund: "Refund",
        reversal: "Reversal",
      }) as const
    )[props.transaction.type]
)

const statusLabel = computed(() =>
  pascalize(props.transaction.status)
)

const onClickRefund = async () => {
  if (!supervisorLoginDrawerRef.value) return

  if (!isSupervisor.value) {
    const { isCanceled } =
      await supervisorLoginDrawerRef.value.reveal()

    if (isCanceled) return
  }

  step.value = "refund"
}

const isTransactionVoidable = computed(() => {
  const isCommonVoidableTrait =
    import.meta.env.VITE_PLATFORM !== "ingenicoNerpay" &&
    props.transaction.status === "successful" &&
    DateFns.isToday(props.transaction.createdAt) &&
    props.transaction.transactionMethod === "pos_card" &&
    props.transaction.deviceSerialNumber ===
      serialNumber.value

  const isPaymentVoidable =
    props.transaction.type === "payment" &&
    (!("refunds" in props.transaction) ||
      ("refunds" in props.transaction &&
        props.transaction.refunds.every(
          (refund) => refund.status === "voided"
        )))

  return (
    isCommonVoidableTrait &&
    (props.transaction.type === "refund" ||
      isPaymentVoidable)
  )
})

const onClickVoid = async () => {
  if (!supervisorLoginDrawerRef.value) return

  if (!isSupervisor.value) {
    const { isCanceled } =
      await supervisorLoginDrawerRef.value.reveal()

    if (isCanceled) return
  }

  try {
    startLoading()
    if (
      "uti" in props.transaction &&
      props.transaction.uti
    ) {
      const a920Response = await IPayment.makeDeviceVoid({
        uti: props.transaction.uti,
        transactionAmount:
          props.transaction.amountTransactedTotal,
        disablePrinting: false,
      })

      if (a920Response.status === "approved") {
        try {
          await voidTransaction(
            props.transaction.id,
            a920Response.data
          )
        } catch {
          toast.error(
            "Void successful but has failed to sync with server. Please do not try again.",
            10000
          )
        }
      } else if (a920Response.status === "cancelled") {
        emit("close")
        return
      } else if (a920Response.status === "not_possible") {
        throw new Error(
          "Void not possible. Transaction has already settled."
        )
      } else {
        throw new Error("Something went wrong")
      }

      emit("close")
    }
  } catch (error) {
    stopLoading()
    Sentry.captureException(error)

    if (
      (error as Error).message === "Invalid slip number"
    ) {
      toast.error("Transaction not found on terminal", 5000)
    } else if (
      (error as Error).message ===
      "Void not possible. Transaction has already settled."
    ) {
      toast.error((error as Error).message, 5000)
    } else {
      toast.error("Something went wrong")
    }

    if (
      refundConfirmationRef.value &&
      props.transaction.type === "payment"
    ) {
      const { isCanceled: refundIsCancelled } =
        await refundConfirmationRef.value.reveal()
      if (!refundIsCancelled) {
        await onClickRefund()
        return
      }
    }

    emit("close")
  } finally {
    stopLoading()
  }
}

const maybePrintMerchantReceipt = async (
  transactionId: string
) => {
  if (
    tenant.isAutomaticMerchantReceiptEnabled &&
    printingSupported.value
  ) {
    startLoading()
    try {
      const { receipt } = await retrieveTransactionReceipt(
        transactionId,
        {
          isMerchant: true,
        }
      )

      await IPrinter.printStr({ str: receipt })
    } catch {
      toast.error("Failed to generate receipt")
    } finally {
      stopLoading()
    }
  }
}
</script>
